import { createRef, PureComponent } from 'react';
import { NavMarker } from 'react-rhumb';
import I18n from 'I18n';
import Raven from 'raven-js';
import { MessageTypes } from 'video-embed/constants';
import { ConversionAssetPositions, PlayerTypes } from 'video-embed/types';
import { debugLog } from 'video-embed/utils/debug';
import { pick } from 'video-embed/utils/object';
import { PLAYER_OPTIONS, PlayerStatus, VIDEOJS_LOAD_TIMEOUT_MS } from '../constants';
import { getStandaloneTracker } from '../utils/tracker';
import createPlayer, { getHlsTech, HLS_LAZY_LOAD_DELAY } from '../utils/player';
import { setupPlayButton } from '../utils/playButton';
import { getSecondsElapsed } from '../utils/tracker';
import { Metrics } from '../utils/metrics';
import { Floating } from './Floating';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PIP_SESSION_STORAGE_KEY = 'hs-video-player-pip-player-state';
const PIP_STATE_CLEARED_KEY = 'PIP-SESSION-STATE-CLEARED';
const IGNORE_FOCUSED_TAGS = ['INPUT', 'TEXTAREA', 'A', 'BUTTON', 'CONTENTEDITABLE'];
export default class VideoJSWrapper extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      isClosed: false
    };
    this.videoNode = /*#__PURE__*/createRef();
    this.playerRef = /*#__PURE__*/createRef();
    this.hlsLoadStarted = false;
    this.isPipActive = false;
    this.renderVolumeControlPanel = () => {
      const volumeControlPanel = document.querySelector('.vjs-volume-panel');
      if (volumeControlPanel) {
        volumeControlPanel.classList.add('vjs-hover');
      }
    };
    this.handleKeyDown = e => {
      if (e.target instanceof HTMLElement && (IGNORE_FOCUSED_TAGS.includes(e.target.tagName) || e.target.role === 'slider')) {
        return;
      } else {
        if (e.code === 'ArrowRight') {
          var _this$playerRef$curre, _this$playerRef$curre2;
          (_this$playerRef$curre = this.playerRef.current) === null || _this$playerRef$curre === void 0 || _this$playerRef$curre.currentTime(((_this$playerRef$curre2 = this.playerRef.current) === null || _this$playerRef$curre2 === void 0 ? void 0 : _this$playerRef$curre2.currentTime()) + 3);
        } else if (e.code === 'ArrowLeft') {
          var _this$playerRef$curre3, _this$playerRef$curre4;
          (_this$playerRef$curre3 = this.playerRef.current) === null || _this$playerRef$curre3 === void 0 || _this$playerRef$curre3.currentTime(((_this$playerRef$curre4 = this.playerRef.current) === null || _this$playerRef$curre4 === void 0 ? void 0 : _this$playerRef$curre4.currentTime()) - 3);
        } else if (e.code === 'ArrowUp') {
          var _this$playerRef$curre5, _this$playerRef$curre6;
          e.preventDefault();
          (_this$playerRef$curre5 = this.playerRef.current) === null || _this$playerRef$curre5 === void 0 || _this$playerRef$curre5.volume(((_this$playerRef$curre6 = this.playerRef.current) === null || _this$playerRef$curre6 === void 0 ? void 0 : _this$playerRef$curre6.volume()) + 0.1);
          this.renderVolumeControlPanel();
        } else if (e.code === 'ArrowDown') {
          var _this$playerRef$curre7, _this$playerRef$curre8;
          e.preventDefault();
          (_this$playerRef$curre7 = this.playerRef.current) === null || _this$playerRef$curre7 === void 0 || _this$playerRef$curre7.volume(((_this$playerRef$curre8 = this.playerRef.current) === null || _this$playerRef$curre8 === void 0 ? void 0 : _this$playerRef$curre8.volume()) - 0.1);
          this.renderVolumeControlPanel();
        } else if (e.code === 'Space') {
          var _this$playerRef$curre9;
          if ((_this$playerRef$curre9 = this.playerRef.current) !== null && _this$playerRef$curre9 !== void 0 && _this$playerRef$curre9.paused()) {
            var _this$playerRef$curre10;
            (_this$playerRef$curre10 = this.playerRef.current) === null || _this$playerRef$curre10 === void 0 || (_this$playerRef$curre10 = _this$playerRef$curre10.play()) === null || _this$playerRef$curre10 === void 0 || _this$playerRef$curre10.catch(console.error);
          } else {
            var _this$playerRef$curre11;
            (_this$playerRef$curre11 = this.playerRef.current) === null || _this$playerRef$curre11 === void 0 || _this$playerRef$curre11.pause();
          }
        } else if (e.code === 'KeyM') {
          const player = this.playerRef.current;
          if (player) {
            player.muted(!player.muted());
          }
        } else if (e.code === 'KeyF') {
          var _this$playerRef$curre12;
          if ((_this$playerRef$curre12 = this.playerRef.current) !== null && _this$playerRef$curre12 !== void 0 && _this$playerRef$curre12.isFullscreen()) {
            var _this$playerRef$curre13;
            (_this$playerRef$curre13 = this.playerRef.current) === null || _this$playerRef$curre13 === void 0 || _this$playerRef$curre13.exitFullscreen();
          } else {
            var _this$playerRef$curre14;
            (_this$playerRef$curre14 = this.playerRef.current) === null || _this$playerRef$curre14 === void 0 || _this$playerRef$curre14.requestFullscreen();
          }
        }
      }
    };
    this.setCurrentTime = async currentTime => {
      const player = this.playerRef.current;
      if (!player.hasStarted()) {
        // to be sure the player is ready for currentTime to be set, or else can get stuck showing poster image instead of controls
        debugLog('Force starting player to enable setting currentTime');
        this.startHlsLoad('set current time');
        // based on https://stackoverflow.com/a/24211292
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          await player.play();
          player.pause();
        } catch (err) {
          debugLog('Failed to play player to enable setting currentTime', err);
        }
      }
      player.currentTime(currentTime);
    };
    this.handleWaiting = () => {
      const {
        options,
        status
      } = this.props;
      if (!this.playerRef.current || status === PlayerStatus.LOADING || status === PlayerStatus.CONVERSION_ASSET_OVERLAY) {
        return;
      }
      // autoplay tends to cause thrashing between playing and loading states,
      // so only allow loading before the initial play
      if (options.autoplay && this.playerRef.current.hasStarted()) {
        return;
      }
      this.props.onChangeStatus(PlayerStatus.LOADING);
      this.props.postMessageToParent(MessageTypes.PLAYER_LOADING);
    };
    this.handleReady = () => {
      const {
        options
      } = this.props;
      const player = this.playerRef.current;
      this.configureHls();
      this.props.onChangeStatus(PlayerStatus.READY);
      this.props.onReady();
      if (options.autoplay) {
        setTimeout(() => {
          player.play().catch(err => {
            debugLog('Failed to autoplay, muting and retrying', err);
            setTimeout(() => {
              player.muted(true);
              player.play().catch(_err => debugLog('Failed to autoplay after muting:', _err));
            });
          });
        });
      }

      // prevent race condition where text tracks added before player load and end up missing, see https://issues.hubspotcentral.com/browse/CG-43176?focusedCommentId=2890771
      if (this.props.captions) {
        this.initCaptions();
      }
    };
    this.startHlsLoad = reason => {
      const player = this.playerRef.current;
      const hls = getHlsTech(player);
      if (!hls) {
        return;
      }
      if (!this.hlsLoadStarted) {
        this.props.trackUsage('hlsLoadStarted', {
          reason,
          secondsElapsed: getSecondsElapsed()
        });
        hls.startLoad(-1);
      }
      this.hlsLoadStarted = true;
      if (this.hlsLoadTimeout) {
        clearTimeout(this.hlsLoadTimeout);
      }
    };
    this.initCaptions = () => {
      const {
        captions,
        trackUsage,
        options: {
          subtitleLanguage
        }
      } = this.props;
      const player = this.playerRef.current;
      if (player.textTracks().length) {
        debugLog('Text tracks already initialized in initCaptions');
        return;
      }
      captions.forEach(({
        lang,
        label,
        url
      }) => {
        const track = {
          kind: 'captions',
          label,
          src: url,
          srclang: lang,
          mode: subtitleLanguage === lang ? 'showing' : 'hidden'
        };
        debugLog('Adding text track:', track);
        player.addRemoteTextTrack(track, false);
      });
      const captionsButton = document.querySelector('button.vjs-captions-button');
      if (captionsButton) {
        captionsButton.addEventListener('click', e => {
          this.insertSubtitlesPopoverHeader();
          player.toggleClass('captions-toggle');
          if (e.target instanceof Element && captionsButton.contains(e.target)) {
            trackUsage('playerInteraction', {
              action: captionsButton.getAttribute('aria-expanded') === 'true' ? 'open-subtitles-popover' : 'close-subtitles-popover'
            });
          }
        });
      }
    };
    this.insertSubtitlesPopoverHeader = () => {
      const captionsMenuEl = document.querySelector('.vjs-captions-button .vjs-menu-content');
      if (captionsMenuEl && !captionsMenuEl.querySelector('.subtitles-header')) {
        debugLog('Inserting subtitles menu node', captionsMenuEl);
        captionsMenuEl.insertAdjacentHTML('afterbegin', `<label class="subtitles-header">${I18n.text('videoPlayer.controls.captions')}</label>`);
        captionsMenuEl.insertAdjacentHTML('afterend', `<div class="popover-triangle" />`);
      }
    };
    this.handlePlaying = () => {
      const {
        status,
        hasConverted
      } = this.props;
      const player = this.playerRef.current;
      if (status === PlayerStatus.CONVERSION_ASSET_OVERLAY && !hasConverted) {
        debugLog('Refusing to play in CONVERSION_ASSET_OVERLAY state');
        player.pause();
        return;
      }
      if (status === PlayerStatus.PLAYING) {
        return;
      }
      this.props.onChangeStatus(PlayerStatus.PLAYING);
      this.props.onPlaying();
    };
    this.handlePause = () => {
      const {
        status
      } = this.props;
      if (status !== PlayerStatus.CONVERSION_ASSET_OVERLAY) {
        this.props.onChangeStatus(PlayerStatus.PAUSED);
        this.props.onPause();
      }
    };
    this.handleEnded = () => {
      const {
        options
      } = this.props;
      this.props.onChangeStatus(PlayerStatus.ENDED);
      this.props.onEnded();
      const player = this.playerRef.current;
      if (options.conversionAsset && options.conversionAsset.position === ConversionAssetPositions.POST && player.isFullscreen()) {
        player.exitFullscreen();
      }
    };
    this.handleVisibilityStateChange = () => {
      const {
        embedId
      } = this.props;
      const player = this.playerRef.current;
      if (player.paused() && document.visibilityState === 'hidden') {
        // modern browsers sometimes automatically pause then resume videos without sound on visibility change,
        // in this don't interfere https://developer.chrome.com/blog/media-updates-in-chrome-63-64/#background-video-track-optimizations
        debugLog(`Player ${embedId} is already paused on visibilityState change to ${document.visibilityState}`);
        this.props.trackUsage('playerInteraction', {
          action: 'paused-for-background-tab'
        });
        document.removeEventListener('visibilitychange', this.handleVisibilityStateChange);
      } else if (document.visibilityState === 'hidden') {
        debugLog(`Pausing player ${embedId} for visibilityState change to ${document.visibilityState}`);
        // todo don't track duplicate `paused` action somehow
        this.props.trackUsage('playerInteraction', {
          action: 'paused-for-visibility-hidden'
        });
        player.pause();
        document.removeEventListener('visibilitychange', this.handleVisibilityStateChange);
      }
    };
    this.handlePlayerError = () => {
      var _err$hlsError, _err$hlsError2;
      const {
        video,
        playerType
      } = this.props;
      const player = this.playerRef.current;
      const err = player.error();
      const extra = {
        errorMessage: err ? err.message : String(err),
        seeking: player.seeking(),
        scrubbing: player.scrubbing(),
        bufferedPercent: player.bufferedPercent(),
        currentTime: player.currentTime(),
        hasStarted: player.hasStarted()
      };
      let phase = 'load';
      if (player.hasStarted()) {
        if (extra.currentTime > 0) {
          phase = 'playing';
        } else {
          phase = 'initial play';
        }
      }
      const fileExtension = video.fileUrl.split('.').at(-1);
      const streamType = video.primaryPlaybackId ? 'video/mux' : `video/${fileExtension}`;
      Metrics.counter('videojs-error', {
        phase,
        playerType,
        streamType,
        isEmbeddable: String(video.isEmbeddable),
        transcodingState: video.transcodingState,
        errorCode: String(err === null || err === void 0 ? void 0 : err.code) || 'none',
        errorMessage: (err === null || err === void 0 ? void 0 : err.message) || 'none',
        hasHlsError: String(Boolean(err === null || err === void 0 ? void 0 : err.hlsError)),
        hlsErrorStatusCode: String(err === null || err === void 0 || (_err$hlsError = err.hlsError) === null || _err$hlsError === void 0 || (_err$hlsError = _err$hlsError.response) === null || _err$hlsError === void 0 ? void 0 : _err$hlsError.code) || 'none',
        hlsErrorDetails: (err === null || err === void 0 || (_err$hlsError2 = err.hlsError) === null || _err$hlsError2 === void 0 ? void 0 : _err$hlsError2.details) || 'none'
      }).increment();

      // Track all player errors to Amplitude
      const standaloneTracker = getStandaloneTracker();
      const videoHostingProvider = video.primaryPlaybackId ? 'mux' : 'raw';
      const action = err !== null && err !== void 0 && err.hlsError ? 'hls-fallback-to-raw-video' : 'player-error';
      standaloneTracker.track('Video Player', 'Player Error', {
        action,
        error: (err === null || err === void 0 ? void 0 : err.message) || 'unknown',
        errorCode: String(err === null || err === void 0 ? void 0 : err.code) || 'unknown',
        videoHostingProvider,
        phase
      });
      if (err !== null && err !== void 0 && err.hlsError) {
        Raven.captureMessage('Hit videojs error with hls info', {
          extra: err
        });
        Metrics.counter('hls-fallback-to-raw-video').increment();
        player.src({
          src: video.mp4FileUrl || video.fileUrl
        });
        debugLog('Updated videojs player src to', player.src());
        return;
      }
      this.props.onChangeStatus(PlayerStatus.ERROR, err);
    };
    this.handleClick = e => {
      // clicking on the video itself via `userActions.click` config does not work w/ hiddenControls,
      // so you can click the "big play button" to play but then there is no way to pause
      const player = this.playerRef.current;
      const target = e.target;
      if (target.nodeName === 'VIDEO' && !player.controls()) {
        if (player.paused()) {
          var _player$play;
          (_player$play = player.play()) === null || _player$play === void 0 || _player$play.catch(error => {
            throw error;
          });
        } else {
          player.pause();
        }
      }
    };
    this.handleTouchStart = e => {
      // mobile taps on the big play button or control bar work, but make sure tapping on the video itself plays/pauses
      const player = this.playerRef.current;
      const target = e.target;
      if (target.nodeName === 'VIDEO' && player.hasStarted()) {
        if (player.paused()) {
          var _player$play2;
          (_player$play2 = player.play()) === null || _player$play2 === void 0 || _player$play2.catch(error => {
            throw error;
          });
        } else {
          player.pause();
        }
      }
    };
    this.handleProgress = () => {
      const currentTextTrack = this.playerRef.current.textTracks().tracks_.find(textTrack => textTrack.mode === 'showing');
      const textLanguage = currentTextTrack && currentTextTrack.language;
      this.props.postMessageToParent(MessageTypes.PLAYER_PROGRESS, {
        currentTime: this.playerRef.current.currentTime(),
        textLanguage
      });
    };
    this.handleRateChange = () => {
      this.props.postMessageToParent(MessageTypes.SET_PLAYBACK_RATE, {
        playbackRate: this.playerRef.current.playbackRate()
      });
    };
    this.handleAutoplayToggle = () => {
      this.props.postMessageToParent(MessageTypes.TRIGGER_AUTOPLAY);
    };
    this.handleTimeUpdate = () => {
      const currentTime = this.playerRef.current.currentTime();
      this.props.postMessageToParent(MessageTypes.PLAYER_TIME_UPDATE, {
        currentTime
      });
    };
    this.handleSeeked = () => {
      this.props.postMessageToParent(MessageTypes.PLAYER_SEEKED, {
        currentTime: this.playerRef.current.currentTime()
      });
    };
    this.handleEnterPictureInPicture = () => {
      const currentTime = this.playerRef.current.currentTime();
      this.isPipActive = true;
      this.props.postMessageToParent(MessageTypes.TRIGGER_PICTURE_IN_PICTURE, {
        isPictureInPictureActive: true,
        currentTime
      });
    };
    this.handleLeavePictureInPicture = () => {
      this.isPipActive = false;
      this.props.postMessageToParent(MessageTypes.TRIGGER_PICTURE_IN_PICTURE, {
        isPictureInPictureActive: false,
        currentTime: this.playerRef.current.currentTime()
      });
    };
    this.handlePageHide = () => {
      var _window$top;
      if (!(this.isPipActive || this.props.options.playerType === PlayerTypes.FLOATING_PLAYER) || !this.props.options.enablePictureInPicturePersistence) {
        return;
      }
      const player = this.playerRef.current;
      if (!player) return;
      sessionStorage.setItem(PIP_SESSION_STORAGE_KEY, JSON.stringify(Object.assign({}, this.props.options, {
        currentTime: player.currentTime(),
        muted: player.muted(),
        startedAt: new Date().getTime()
      })));
      (_window$top = window.top) === null || _window$top === void 0 || _window$top.postMessage({
        type: 'PIP_PLAYER_UNLOADED'
      });
    };
    this.handleCloseFloatingMessage = event => {
      var _event$data;
      if (((_event$data = event.data) === null || _event$data === void 0 ? void 0 : _event$data.type) === MessageTypes.CLOSE_FLOATING_PLAYER) {
        this.handleFloatingClose();
      }
    };
    this.handleFloatingClose = () => {
      var _this$playerRef$curre15;
      sessionStorage.removeItem(PIP_SESSION_STORAGE_KEY);
      if ('serviceWorker' in navigator) {
        var _navigator$serviceWor;
        (_navigator$serviceWor = navigator.serviceWorker.controller) === null || _navigator$serviceWor === void 0 || _navigator$serviceWor.postMessage({
          type: PIP_STATE_CLEARED_KEY
        });
      }
      document.removeEventListener('visibilitychange', this.handleVisibilityStateChange);
      document.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('pagehide', this.handlePageHide);
      window.removeEventListener('message', this.handleCloseFloatingMessage);
      (_this$playerRef$curre15 = this.playerRef.current) === null || _this$playerRef$curre15 === void 0 || _this$playerRef$curre15.dispose();
      this.playerRef.current = null;
      this.setState({
        isClosed: true
      });
    };
  }
  componentDidMount() {
    const {
      video,
      options,
      pageMeta,
      trackUsage
    } = this.props;
    const playerOptions = this.getPlayerOptions();
    const pageUrl = pageMeta ? pageMeta.pageUrl : options.parentOrigin;
    const muxDataProps = {
      pageUrl,
      renderContext: options.renderContext
    };
    const player = createPlayer(this.videoNode.current, playerOptions, video, muxDataProps);
    this.playerRef.current = player;
    debugLog('Created videojs player', options, player, muxDataProps);
    setupPlayButton(player, options);
    player.errors({
      timeout: VIDEOJS_LOAD_TIMEOUT_MS
    });
    player.on(['aderror', 'contenterror', 'error'], this.handlePlayerError);
    player.on('waiting', this.handleWaiting);
    player.one('ready', this.handleReady);
    player.on('playing', this.handlePlaying);
    player.on('pause', this.handlePause);
    player.on('seeked', this.handleSeeked);
    player.on('ended', this.handleEnded);
    player.on('click', this.handleClick);
    player.on('touchstart', this.handleTouchStart);
    player.on('progress', this.handleProgress);
    player.on('timeupdate', this.handleTimeUpdate);
    player.on('ratechange', this.handleRateChange);
    player.on('autoplaytoggle', this.handleAutoplayToggle);
    player.on('texttrackchange', event => {
      const currentTextTrack = event.target.player.textTracks().tracks_.find(textTrack => textTrack.mode === 'showing');
      const language = currentTextTrack && currentTextTrack.language;
      if (language) {
        trackUsage('playerInteraction', {
          action: `text-track-change-${language}`
        });
      }
      this.handleProgress();
    });
    player.on('enterpictureinpicture', this.handleEnterPictureInPicture);
    player.on('leavepictureinpicture', this.handleLeavePictureInPicture);
    if (options.enableVideoFrameHotKeys) {
      document.addEventListener('keydown', this.handleKeyDown);
    }
    if (playerOptions.autoplay || playerOptions.loop) {
      document.addEventListener('visibilitychange', this.handleVisibilityStateChange);
    }
    if (typeof this.props.currentTime === 'number') {
      this.setCurrentTime(this.props.currentTime).catch(console.error);
    }
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('message', this.handleCloseFloatingMessage);
  }
  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityStateChange);
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('message', this.handleCloseFloatingMessage);
  }
  componentDidUpdate(prevProps) {
    const {
      confirmedVisible,
      currentTime,
      waitForConfirmedVisible,
      captions
    } = this.props;
    if (!prevProps.captions && captions) {
      this.initCaptions();
    }
    if (waitForConfirmedVisible && confirmedVisible && !prevProps.confirmedVisible) {
      this.startHlsLoad('confirmed-visible');
    }
    if (typeof currentTime === 'number' && currentTime !== prevProps.currentTime) {
      this.setCurrentTime(currentTime).catch(console.error);
    }
  }
  getPlayer() {
    return this.playerRef.current;
  }
  captureFrame() {
    return new Promise((resolve, reject) => {
      const videoEl = this.videoNode.current;
      if (!videoEl) {
        reject(new Error('Failed to find video element'));
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to create canvas'));
        return;
      }
      try {
        ctx.drawImage(videoEl, 0, 0);
      } catch (_unused) {
        reject(new Error('Failed to draw thumbnail'));
        return;
      }
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Failed to encode blob for canvas'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.92);
    });
  }
  getPlayerOptions() {
    const {
      options,
      playerType
    } = this.props;
    const playerOptions = pick(options, PLAYER_OPTIONS);
    if (playerOptions.loop && options.conversionAsset && options.conversionAsset.position === ConversionAssetPositions.POST) {
      // necessary to see asset overlay, we'll enable looping manually after overlay is skipped or asset is clicked/submitted
      playerOptions.loop = false;
    }
    playerOptions.enablePictureInPicture = options.enablePictureInPicture !== false && playerType !== PlayerTypes.ACADEMY_EMBEDDED &&
    // The floating player is itself the PiP surface — no PiP-of-a-PiP.
    // Forced here since the embed loader strips the loader's falsy
    // enablePictureInPicture before it reaches the iframe.
    playerType !== PlayerTypes.FLOATING_PLAYER;
    return playerOptions;
  }
  configureHls() {
    const {
      options,
      playerType
    } = this.props;
    const player = this.playerRef.current;
    const hls = getHlsTech(player);
    if (!hls) {
      debugLog('configureHls called before tech exists');
      return;
    }
    debugLog(`configureHls called with loadLevel: ${hls.loadLevel}`);
    if (options.autoplay) {
      this.startHlsLoad('autoplay');
    } else {
      this.configureLazy();
    }
    if (playerType === PlayerTypes.COMPACT_PLAYER || playerType === PlayerTypes.COMPACT_PLAYER_WITH_CUSTOM_MENU) {
      hls.currentLevel = 0;
    }
  }
  configureLazy() {
    const player = this.playerRef.current;
    const hls = getHlsTech(player);
    if (!hls) {
      return;
    }
    if (!this.props.waitForConfirmedVisible) {
      this.hlsLoadTimeout = setTimeout(() => {
        if (player.hasStarted()) {
          return;
        }
        this.startHlsLoad('defer timeout');
      }, HLS_LAZY_LOAD_DELAY);
    } else if (this.props.confirmedVisible) {
      this.startHlsLoad('initially-visible');
    }
    player.one('play', () => this.startHlsLoad('play'));
    player.one('seeking', () => this.startHlsLoad('seeking'));
  }
  renderPlayer() {
    const {
      status,
      options
    } = this.props;
    const objectFit = options.objectFit === 'cover' ? options.objectFit : 'contain';
    // The floating player portals its video into the host page via <Floating>,
    // so EmbedContainer's body[data-player-type] isn't an ancestor. Set it on
    // the wrapper instead so _floating-player.sass can target it.
    const isFloatingPlayer = options.playerType === PlayerTypes.FLOATING_PLAYER;
    return /*#__PURE__*/_jsxs("div", Object.assign({
      "data-vjs-player": "true",
      "data-status": status.toLowerCase(),
      "data-has-poster-image": !options.hidePosterImage,
      "data-test-id": "video-js-wrapper"
    }, isFloatingPlayer ? {
      'data-player-type': options.playerType
    } : {}, {
      children: [/*#__PURE__*/_jsx("video", {
        ref: this.videoNode,
        className: "video-js vjs-hubspot vjs-big-play-centered vjs-mux videojs-mux-player-dimensions",
        controls: true
        // ensures subtitle button is initially visible in Safari, see https://issues.hubspotcentral.com/browse/CG-43101
        ,
        crossOrigin: "anonymous",
        playsInline: true,
        preload: "none",
        style: {
          objectFit
        }
      }), /*#__PURE__*/_jsx(NavMarker, {
        name: "PLAYER_LOADED"
      })]
    }));
  }
  render() {
    const {
      options
    } = this.props;
    if (options.playerType === PlayerTypes.FLOATING_PLAYER) {
      if (this.state.isClosed) return null;
      return /*#__PURE__*/_jsx(Floating, {
        onClose: this.handleFloatingClose,
        children: this.renderPlayer()
      });
    }
    return this.renderPlayer();
  }
}
VideoJSWrapper.defaultProps = {
  status: PlayerStatus.INITIAL,
  onChangeStatus: () => {},
  onReady: () => {},
  onPlaying: () => {},
  onPause: () => {},
  onEnded: () => {}
};